<template>
  <HeadTitle
    title="Crear configuración de carta"
    description="Formulario de creación y configuración de cartas y certificaciones"
  />
  <br />
  <Step :stepNumber="1" title="Configuración inicial">
    <div class="formgrid grid w-12 md:w-6">
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="name">Nombre <span class="text-red-600">*</span></label>
        <InputText v-model="name" id="name" type="text" />
        <small id="text-error" class="p-error">{{
          nameError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="description"
          >Descripción <span class="text-red-600">*</span></label
        >
        <InputText id="description" v-model="description" />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="generationType"
          >Método de generación <span class="text-red-600">*</span></label
        >
        <Dropdown
          id="generationType"
          v-model="generationType"
          :options="approvalMethodsOpts"
          optionLabel="label"
          optionValue="value"
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          generationTypedError || '&nbsp;'
        }}</small>
      </div>
      <div
        class="field col-10 md:col-6 flex flex-column"
        v-if="generationType === 'WITH_APPROVAL'"
      >
        <label for="approver"
          >Seleccionar Aprobador <span class="text-red-600">*</span></label
        >
        <Dropdown
          v-model="approver"
          :options="approvers"
          optionLabel="name"
          optionValue="id"
          filter
        ></Dropdown>
        <small id="text-error" class="p-error">{{
          approverError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-6 flex flex-column">
        <label for="approver"
          >Seleccionar nóminas <span class="text-red-600">*</span></label
        >
        <MultiSelectDialog
          header-title="Seleccionar nóminas"
          label="Seleccionar nóminas"
          :options="payrolls || []"
          v-model="selectedPayrolls"
          show-key="name"
          value-key="id"
          :required="true"
          :disabled="false"
        />
        <small id="text-error" class="p-error">{{
          payrollsError || '&nbsp;'
        }}</small>
      </div>
      <div class="field col-10 md:col-8 flex flex-column">
        <div class="flex flex-row mb-2">
          <label for="approver">Correos a notificar</label>
          <InfoButton
            message="Se enviara un correo con copia del archivo a los emails seleccionados cuando la solicitud de carta sea generada."
          />
        </div>
        <Chips v-model="emailsToNotify" separator="," class="w-12" invalid />
        <small id="text-error" class="p-error">{{
          emailsError || '&nbsp;'
        }}</small>
      </div>
    </div>
  </Step>
  <Step
    :stepNumber="2"
    title="Configurar aprobadores"
    v-if="generationType === 'WITH_MULTIPLE_APPROVALS'"
  >
    <LetterApproversForm
      v-model="approversData"
      :approvers-options="approvers || []"
      :max-items="MAX_APPROVERS"
      :disabled="isSubmitting"
      :error-message="approversError"
    />
  </Step>
  <!--Editor step-->
  <Step
    :stepNumber="generationType === 'WITH_MULTIPLE_APPROVALS' ? 3 : 2"
    title="Preparar documento"
  >
    <Button
      label="Abrir editor"
      class="text-sm font-light"
      icon="pi pi-window-maximize"
      @click="isEditorModalVisible = !isEditorModalVisible"
    ></Button>
  </Step>
  <Footer>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="router.back()"
    />
    <Button
      label="Guardar"
      class="text-sm font-light"
      severity="primary"
      @click="onSave"
      :disabled="!isValidToSave"
    />
  </Footer>
  <Dialog
    v-model:visible="isEditorModalVisible"
    class="p-dialog-maximized"
    modal
  >
    <template #header>
      <HeadTitle
        title="Editor de carta"
        description="Editor de carta para personalizar el contenido de la carta"
      />
    </template>
    <div class="flex flex-column h-full">
      <Editor
        ref="letterEditorComponent"
        :template="template"
        :page-properties="{ margins: pageMargins }"
      />
    </div>
    <template #footer>
      <FooterEditor
        ref="footerEdit"
        @save-editor-content="onSaveEditorContent"
        @on-preview="onPreview"
        :header-img="headerImg"
        :footer-img="footerImg"
      />
    </template>
  </Dialog>
</template>
<script lang="ts" setup>
  import Step from '@/shared/components/Step.vue'
  import { useRouter } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import Editor from '../components/Editor.vue'
  import InfoButton from '@/shared/components/InfoButton.vue'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { useField } from 'vee-validate'
  import * as yup from 'yup'
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import Approver from '@/modules/approvers/models/Approver'
  import { getApproversByCompanyId } from '@/modules/approvers/services/ApproverService'
  import useCompany from '@/store/company'
  import { storeToRefs } from 'pinia'
  import Payroll from '@/modules/payrolls-config/models/Payroll'
  import { getPayrollsByCompanyId } from '@/modules/payrolls-config/services/PayrollService'
  import useNotifications from '@/store/notification'
  import useLoading from '@/store/load'
  import {
    createLetterConfig,
    getPreviewLetter,
  } from '../services/LetterConfgServices'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import FooterEditor from '../components/FooterEditor.vue'
  import LetterApproversForm from '../components/LetterApproversForm.vue'
  import { ILetterApproverDetail } from '../services/dtos/ILetterApproverDetail'
  const router = useRouter()
  const companyStore = useCompany()
  const loadingStore = useLoading()
  const notificationStore = useNotifications()
  const { company } = storeToRefs(companyStore)
  const letterEditorComponent = ref<any>(null)
  const footerEdit = ref()
  const headerImg = ref()
  const footerImg = ref()

  const isSubmitting = ref(false)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    await fetchApprovers()
    await fetchPayrolls()
  })

  const approvalMethodsOpts = ref([
    { label: 'Requiere aprobación', value: 'WITH_APPROVAL' },
    {
      label: 'Requiere Multiple aprobaciones',
      value: 'WITH_MULTIPLE_APPROVALS',
    },
    { label: 'Automática', value: 'AUTOMATIC' },
  ])

  const approvers = ref<Approver[]>()
  const payrolls = ref<Payroll[]>()
  const MAX_APPROVERS = 8

  const nameSchema = yup.string().required('El nombre es requerido')
  const descriptionSchema = yup.string().required('La descripción es requerida')
  const generationTypeSchema = yup
    .string()
    .required('El método de aprobación es requerido')
  const approversSchema = yup.array(
    yup.object().shape({
      approverId: yup.string().required('Selecciona un aprobador'),
      approverName: yup
        .string()
        .required('El nombre del aprobador es requerido'),
      approverPosition: yup.string().optional().nullable(),
      approverPhone: yup.string().optional().nullable(),
      approverEmail: yup.string().optional().nullable(),
    })
  )

  // for single approver selection
  const approverSchema = yup.string().required('El aprobador es requerido')

  const {
    value: approver,
    errorMessage: approverError,
    validate: approverValidate,
  } = useField<string>('approver', approverSchema)

  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')

  const emailsSchema = yup
    .array()
    .test('emails', 'Uno de los correos es invalido', (value) => {
      if (value) {
        const emails = value
        const regex = new RegExp(
          '^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'
        )
        return emails.every((email) => regex.test(email.trim()))
      }
      return true
    })

  const isEditorModalVisible = ref(false)
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: name,
    errorMessage: nameError,
    validate: nameValidate,
  } = useField<string>('description', nameSchema)
  const {
    value: generationType,
    errorMessage: generationTypedError,
    validate: modValidate,
  } = useField<string>('generationType', generationTypeSchema)

  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)

  const {
    value: approversData,
    errorMessage: approversError,
    validate: approversValidate,
  } = useField<ILetterApproverDetail[]>('approversData', approversSchema, {
    initialValue: [],
  })

  approversData.value = approversData.value ?? []

  const createEmptyApprover = (): ILetterApproverDetail => ({
    approverId: '',
    approverName: '',
    approverPosition: null,
    approverPhone: null,
    approverEmail: null,
    signature: undefined,
    signatureUrl: null,
    isDeleted: false,
  })

  const ensureApproverRows = () => {
    if (generationType.value !== 'WITH_MULTIPLE_APPROVALS') {
      approversData.value = []
      return
    }

    if (!approversData.value || approversData.value.length === 0) {
      approversData.value = [createEmptyApprover()]
    }

    if (approversData.value.length > MAX_APPROVERS) {
      approversData.value = approversData.value.slice(0, MAX_APPROVERS)
    }
  }

  const {
    value: emailsToNotify,
    errorMessage: emailsError,
    validate: emailsValidate,
  } = useField<string[]>('emailsToNotify', emailsSchema)
  const template = ref()

  let pageMargins = reactive({
    top: 0.39 * 2,
    bottom: 0.39 * 2,
    left: 0.39 * 2,
    right: 0.39 * 2,
  })

  watch(
    () => generationType.value,
    (value) => {
      if (value === 'WITH_MULTIPLE_APPROVALS') {
        ensureApproverRows()
      } else {
        approversData.value = []
      }
    },
    { immediate: true }
  )

  onMounted(async () => {
    loadingStore.setLoadingState()
    await fetchApprovers()
    await fetchPayrolls()
    loadingStore.dismissLoadingState()
  })

  const fetchApprovers = async () => {
    if (company.value?.id) {
      const reponse = await getApproversByCompanyId(company.value?.id)
      approvers.value = reponse
    } else {
      notificationStore.addErrorNotification({
        detail: `Por favor selecciona una compañia`,
        severity: 'info',
        summary: `No se ha seleccionado una compañia`,
      })
    }
  }

  const fetchPayrolls = async () => {
    if (company.value?.id) {
      const response = await getPayrollsByCompanyId(company.value?.id || '')
      payrolls.value = response.map((payroll) => ({
        ...payroll,
        name: payroll.code + ' - ' + payroll.name,
      }))
    }
  }

  const setEditorContent = () => {
    const { editorRef, margins } = letterEditorComponent.value
    const content = editorRef
    template.value = content
    pageMargins = {
      top: Number(margins.top) / 2.54,
      bottom: Number(margins.bottom) / 2.54,
      left: Number(margins.left) / 2.54,
      right: Number(margins.right) / 2.54,
    }
    headerImg.value = footerEdit.value.headerImg
    footerImg.value = footerEdit.value.footerImg
  }

  const onSaveEditorContent = () => {
    setEditorContent()
    isEditorModalVisible.value = false
  }

  const onPreview = async () => {
    loadingStore.setLoadingState()
    setEditorContent()
    const buffer = await getPreviewLetter({
      template: template.value,
      header: headerImg.value,
      footer: footerImg.value,
      pageProperties: {
        margins: pageMargins,
      },
    })
    loadingStore.dismissLoadingState()

    const blob = new Blob([buffer], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank')
  }

  const onSave = async () => {
    await nameValidate()
    await descValidate()
    await modValidate()
    if (generationType.value === 'WITH_MULTIPLE_APPROVALS') {
      await approversValidate()
    } else {
      approversData.value = []
    }
    await payrollsValidate()
    await emailsValidate()

    if (isValidToSave.value && company.value) {
      loadingStore.setLoadingState()
      isSubmitting.value = true
      const primaryApproverId =
        generationType.value === 'WITH_APPROVAL' ? approver.value : undefined
      const response = await createLetterConfig({
        approverId: primaryApproverId,
        companyId: company.value?.id,
        description: description.value,
        generationType: generationType.value,
        name: name.value,
        payrollsIds: selectedPayrolls.value,
        emailsToNotify: emailsToNotify.value,
        template: template.value,
        header: headerImg.value,
        footer: footerImg.value,
        pageProperties: {
          margins: pageMargins,
        },
        letterApprovers:
          generationType.value === 'WITH_MULTIPLE_APPROVALS'
            ? approversData.value?.map(
                ({ signature, signatureUrl, ...rest }) => ({
                  ...rest,
                  signature,
                })
              )
            : [],
      })
      loadingStore.dismissLoadingState()
      isSubmitting.value = false
      if (response) {
        new Promise((resolve) => {
          return resolve(
            notificationStore.addSuccesNotification({
              detail: `201`,
              severity: 'success',
              summary: `Configuración de carta creada con exito`,
            })
          )
        }).then(() => {
          router.push('/letterConfigs')
        })
      }
    }
  }

  //singature upload helpers
  const isValidToSave = computed(() => {
    return true
  })
</script>
<style scoped>
  :deep(.p-button-label) {
    font-size: 0.8rem;
  }
</style>
